import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IOptionType } from '@zz2/zz2-ui';
import { IBarcodeRange } from './barcodeRange';
import { IBarcodeRangeFormValues } from './barcodeRangeFormValues';
import { IBarcodeRangeUpsert } from './barcodeRangeUpsert';
import { ISite } from '../site/site';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class BarcodeRangeModelHelper {
    public static createFormValues = (
        barcodeRange : IBarcodeRange | null,
        sites : Array<ISite>,
    ) : IBarcodeRangeFormValues => {
        if (!barcodeRange) return {
            id: 0,
            guid: uuidV4(),
            name: '',
            indicatorDigit: null,
            rangeStart: 1,
            rangeEnd: 999999,
            isExport: false,
            latest: null,
            numRemaining: null,
            importCode: null,
            sites: [],
            isActive: true,
        };

        const siteOptions = sites
            .filter(x => x.isActive && barcodeRange.siteIds.includes(x.id))
            .map(x => { // TODO: OptionType.FromDataModel
                return {
                    value: x.id,
                    label: `(${x.code}) - ${x.shortDescription}`,
                }
            });

        return {
            id: barcodeRange.id,
            guid: barcodeRange.guid,
            name: barcodeRange.name,
            indicatorDigit: barcodeRange.indicatorDigit,
            rangeStart: barcodeRange.rangeStart,
            rangeEnd: barcodeRange.rangeEnd,
            isExport: barcodeRange.isExport,
            latest: barcodeRange.latest,
            numRemaining: barcodeRange.numRemaining,
            importCode: barcodeRange.importCode,
            sites: siteOptions,
            isActive: barcodeRange.isActive,
        };
    };

    public static createUpsert = (formValues : IBarcodeRangeFormValues) : IBarcodeRangeUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            name: formValues.name,
            indicatorDigit: formValues.indicatorDigit,
            rangeStart: formValues.rangeStart,
            rangeEnd: formValues.rangeEnd,
            isExport: formValues.isExport,
            latest: formValues.latest,
            numRemaining: formValues.numRemaining,
            importCode: formValues.importCode,
            siteIds: formValues.sites.map(x => Number(x.value)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IBarcodeRangeFormValues> => Yup.object({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        indicatorDigit: Yup.number().defined().nullable()
            .transform((value, originalValue) => 
                !Number(originalValue) ? null : Number(value)
            )
            .when(['isExport'], ([isExport]) => {
                return isExport
                    ? Yup.number()
                        .min(0, 'Value must be more than 0')
                        .max(9, 'Value must be less than 9')
                        .required('Required')
                    : Yup.number().defined().nullable();
            }),
        rangeStart: Yup.number().defined().required('Required'),
        isExport: Yup.boolean().defined().required('Required'),
        rangeEnd: Yup.number().defined().required('Required')
            .when('rangeStart', ([rangeStart]) => {
                return Yup.number()
                    .min(rangeStart, 'Range end must be greater than range start.');
            }),
        importCode: Yup.string().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            )
            .when(['isExport', 'rangeEnd'], ([isExport, rangeEnd]) => {
                return isExport
                    ? Yup.string()
                        .defined()
                        .nullable()
                        .max((16 - rangeEnd.toString().length), 'The combination of range and import code cannot be more than 16 characters long')
                    : Yup.string().defined().nullable();
            }),
        latest: Yup.number().defined().nullable(),
        numRemaining: Yup.number().defined().nullable(),
        sites: Yup.array<IOptionType>().defined().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}